import {
    useState,
    type FC
} from "react";
import {
    type LayoutChangeEvent,
    Platform,
    Animated,
    View
} from "react-native";
import type {
    ProgressIndicatorContentProps,
    ProgressArcProps
} from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    useReducedMotion
} from "../../../../helpers/motion";
import {
    PROGRESS_INDETERMINATE_RATIO,
    PROGRESS_INDETERMINATE_ARC,
    useAnimatedNumber,
    useProgressLoop,
    getEasedRange
} from "../../util";
import {
    Circle,
    Svg
} from "react-native-svg";
import {
    getDirectionSign
} from "../../../../helpers/direction";
import {
    withProgressContext
} from "../../context";
import {
    getProgressRing
} from "../../stylesheet";
import ProgressStep from "../step";

const ProgressArc: FC<ProgressArcProps> = ({
    fraction,
    stroke,
    color,
    ring
}) => {
    if(fraction <= 0) {
        return null;
    }

    const size = ring.center * 2;

    return <Svg
        style={[
            stylesheet.layer,
            stylesheet.arc
        ]}
        height={size}
        width={size}
    >
        <Circle
            strokeDasharray={[
                ring.circumference * fraction,
                ring.circumference
            ]}
            strokeLinecap="round"
            strokeWidth={stroke}
            cx={ring.center}
            cy={ring.center}
            r={ring.radius}
            stroke={color}
            fill="none"
        />
    </Svg>;
};

const IndicatorBar: FC<ProgressIndicatorContentProps> = ({
    context,
    style,
    ...props
}) => {
    const {
        shapeConfigs,
        radiuses,
        spaces
    } = NCoreUIKitTheme.useContext(context.customTheme);

    const fraction = useAnimatedNumber({
        customTheme: context.customTheme,
        value: context.percent
    });

    const {
        bar: barDynamicStyle
    } = useStyles({
        currentColors: context.currentColors,
        shapeConfigs,
        fraction,
        radiuses,
        spaces
    });

    return <View
        {...props}
        style={[
            style,
            barDynamicStyle
        ]}
    />;
};

const AnimatedView = Platform.OS === "web" ? View : Animated.View;

const IndicatorSlide: FC<ProgressIndicatorContentProps> = ({
    onLayout: onLayoutProp,
    context,
    style,
    ...props
}) => {
    const {
        shapeConfigs,
        durations,
        radiuses,
        spaces
    } = NCoreUIKitTheme.useContext(context.customTheme);

    const isReduceMotionEnabled = useReducedMotion();

    const {
        direction
    } = NCoreUIKitLocalize.useContext();

    const [
        width,
        setWidth
    ] = useState<number>(0);

    const slideValue = useProgressLoop({
        isActive: !isReduceMotionEnabled,
        duration: durations.loop,
        shape: "linear"
    });

    const fadeValue = useProgressLoop({
        isActive: isReduceMotionEnabled,
        duration: durations.breathe,
        shape: "breathe"
    });

    const isWeb = Platform.OS === "web";

    const {
        fadeLayer: fadeLayerDynamicStyle,
        webSlide: webSlideDynamicStyle,
        webFade: webFadeDynamicStyle,
        segment: segmentDynamicStyle
    } = useStyles({
        currentColors: context.currentColors,
        fadeDuration: durations.breathe,
        slideDuration: durations.loop,
        shapeConfigs,
        fraction: 0,
        radiuses,
        spaces
    });

    const onLayout = (event: LayoutChangeEvent) => {
        setWidth(event.nativeEvent.layout.width);

        onLayoutProp?.(event);
    };

    const directionSign = getDirectionSign({
        direction
    });

    const translateX = !isWeb && width ? slideValue.interpolate(getEasedRange({
        from: -width * PROGRESS_INDETERMINATE_RATIO * directionSign,
        to: width * directionSign
    })) : 0;

    return <View
        {...props}
        style={[
            style,
            stylesheet.slide,
            isReduceMotionEnabled ? {
                justifyContent: "center"
            } : undefined
        ]}
        onLayout={onLayout}
    >
        <AnimatedView
            style={[
                stylesheet.segment,
                segmentDynamicStyle,
                isReduceMotionEnabled ? undefined : isWeb ? [
                    direction === "rtl" ? stylesheet.webSlideEnd : stylesheet.webSlideStart,
                    webSlideDynamicStyle
                ] : {
                    transform: [
                        {
                            translateX
                        }
                    ]
                }
            ]}
        >
            {
                isReduceMotionEnabled ?
                    <AnimatedView
                        style={[
                            stylesheet.layer,
                            fadeLayerDynamicStyle,
                            isWeb ? [
                                stylesheet.webFade,
                                webFadeDynamicStyle
                            ] : {
                                opacity: fadeValue
                            }
                        ]}
                    />
                :
                    null
            }
        </AnimatedView>
    </View>;
};

const IndicatorSteps: FC<ProgressIndicatorContentProps> = ({
    context,
    style,
    ...props
}) => {
    const {
        shapeConfigs,
        radiuses,
        spaces
    } = NCoreUIKitTheme.useContext(context.customTheme);

    const {
        steps: stepsDynamicStyle
    } = useStyles({
        currentColors: context.currentColors,
        shapeConfigs,
        fraction: 0,
        radiuses,
        spaces
    });

    return <View
        {...props}
        style={[
            style,
            stylesheet.steps,
            stepsDynamicStyle
        ]}
    >
        {
            Array.from({
                length: context.steps
            }, (_, index) => <ProgressStep
                isDone={!context.isIndeterminate && index < context.value}
                key={index}
            />)
        }
    </View>;
};

const IndicatorArc: FC<ProgressIndicatorContentProps> = ({
    context,
    style,
    ...props
}) => {
    const fraction = useAnimatedNumber({
        customTheme: context.customTheme,
        value: context.percent
    });

    return <View
        {...props}
        style={[
            style,
            stylesheet.layer
        ]}
    >
        <ProgressArc
            color={context.currentColors.indicatorColor}
            ring={getProgressRing({
                currentSize: context.currentSize
            })}
            stroke={context.currentSize.stroke}
            fraction={fraction}
        />
    </View>;
};

const IndicatorSpin: FC<ProgressIndicatorContentProps> = ({
    context,
    style,
    ...props
}) => {
    const {
        shapeConfigs,
        durations,
        radiuses,
        spaces
    } = NCoreUIKitTheme.useContext(context.customTheme);

    const isReduceMotionEnabled = useReducedMotion();

    const spinValue = useProgressLoop({
        isActive: !isReduceMotionEnabled,
        duration: durations.spin,
        shape: "linear"
    });

    const fadeValue = useProgressLoop({
        isActive: isReduceMotionEnabled,
        duration: durations.breathe,
        shape: "breathe"
    });

    const isWeb = Platform.OS === "web";

    const {
        webSpin: webSpinDynamicStyle,
        webFade: webFadeDynamicStyle
    } = useStyles({
        currentColors: context.currentColors,
        fadeDuration: durations.breathe,
        spinDuration: durations.spin,
        shapeConfigs,
        fraction: 0,
        radiuses,
        spaces
    });

    const ring = getProgressRing({
        currentSize: context.currentSize
    });

    if(isReduceMotionEnabled) {
        return <View
            {...props}
            style={[
                style,
                stylesheet.layer
            ]}
        >
            <ProgressArc
                color={context.currentColors.indicatorColor}
                fraction={PROGRESS_INDETERMINATE_ARC}
                stroke={context.currentSize.stroke}
                ring={ring}
            />
            <AnimatedView
                style={[
                    stylesheet.layer,
                    isWeb ? [
                        stylesheet.webFade,
                        webFadeDynamicStyle
                    ] : {
                        opacity: fadeValue
                    }
                ]}
            >
                <ProgressArc
                    color={context.currentColors.fadeColor}
                    fraction={PROGRESS_INDETERMINATE_ARC}
                    stroke={context.currentSize.stroke}
                    ring={ring}
                />
            </AnimatedView>
        </View>;
    }

    return <AnimatedView
        {...props}
        style={[
            style,
            stylesheet.layer,
            isWeb ? [
                stylesheet.webSpin,
                webSpinDynamicStyle
            ] : {
                transform: [
                    {
                        rotate: spinValue.interpolate({
                            outputRange: [
                                "0deg",
                                "360deg"
                            ],
                            inputRange: [
                                0,
                                1
                            ]
                        })
                    }
                ]
            }
        ]}
    >
        <ProgressArc
            color={context.currentColors.indicatorColor}
            fraction={PROGRESS_INDETERMINATE_ARC}
            stroke={context.currentSize.stroke}
            ring={ring}
        />
    </AnimatedView>;
};

const ProgressIndicatorContent: FC<ProgressIndicatorContentProps> = ({
    customTheme: customThemeProp,
    context,
    ...props
}) => {
    const currentContext = {
        ...context,
        customTheme: customThemeProp ?? context.customTheme
    };

    if(context.variant === "segmented") {
        return <IndicatorSteps
            {...props}
            context={currentContext}
        />;
    }

    if(context.variant === "circular") {
        return context.isIndeterminate ? <IndicatorSpin
            {...props}
            context={currentContext}
        /> : <IndicatorArc
            {...props}
            context={currentContext}
        />;
    }

    return context.isIndeterminate ? <IndicatorSlide
        {...props}
        context={currentContext}
    /> : <IndicatorBar
        {...props}
        context={currentContext}
    />;
};
export default withProgressContext("Indicator", ProgressIndicatorContent);
